import orderBy from 'lodash/orderBy'
import uniqBy from 'lodash/uniqBy'
import { addHttp } from '@client/utils/addHttp'
import { fontFamilies, webFonts } from '@client/components/Editor/settings'
import { allFonts } from '@client/webfonts-prepared'

export const getFontFormats = (fonts = []) => {
  const filteredFonts = fonts
    .filter((font) => !fontFamilies.find((item) => item.name === font.name))
    .filter((font) => !fontFamilies.find((item) => item.value === font.value))

  const uniqueFonts = uniqBy([...fontFamilies, ...filteredFonts], (i) => i.name)

  return orderBy(uniqueFonts, [(font) => font.name.toLowerCase()], ['asc'])
    .filter((font) => !font.disabled)
    .map((font) => `${font.name}=${font.value}`)
    .join(';')
}

export const getFontContentCss = (fonts = []) => {
  const filteredFonts = fonts
    .filter((font) => !fontFamilies.find((item) => item.name === font.name))
    .filter((font) => !fontFamilies.find((item) => item.value === font.value))
  const fontsString = uniqBy([...webFonts, ...filteredFonts], (i) => i.name)
    .filter((font) => !font.disabled)
    .filter((font) => allFonts.indexOf(font.name) !== -1)
    .map((font) => font.name.trim() + ':400,700')
    .join('|')

  return `//fonts.googleapis.com/css?family=${encodeURIComponent(fontsString)}`
}

const updateStyleAttribute = (attribute, update = {}) => {
  if (!attribute) {
    return attribute
  }
  const attributeStyles = attribute
    .split(';')
    .map((item) => item.split(':'))
    .filter((parts) => parts && parts[0] && parts[1])
    .map((parts) => ({ [parts[0]]: parts[1] }))
    .reduce((result, current) => ({ ...result, ...current }), {})

  const updatedAttributeStyles = {
    ...attributeStyles,
    ...update,
  }

  return Object.keys(updatedAttributeStyles)
    .map((key) => `${key}:${updatedAttributeStyles[key]}`)
    .join(';')
}

export const linkList = [
  { title: 'link_list.viewonline', value: '##view_online##' },
  { title: 'link_list.unsubscribe', value: '##unsubscribe##' },
  { title: 'link_list.updatecontact', value: '##update_contact##' },
  { title: 'link_list.confirmation_link', value: '##confirmation_link##' },
  { title: 'link_list.gdpr', value: '##gdpr_consent##' },
  {
    title: 'link_list.getanewsletter',
    value: 'https://www.getanewsletter.com/',
  },
  { title: 'link_list.tellafriend', value: '##tell_friend##' },
  {
    title: 'link_list.share.facebook',
    value: 'https://www.facebook.com/share.php?u=##newsletter##',
  },
  {
    title: 'link_list.share.twitter',
    value: 'https://x.com/intent/tweet?url=##newsletter##',
  },
  {
    title: 'link_list.share.linkedin',
    value: 'https://www.linkedin.com/shareArticle?mini=true&url=##newsletter##',
  },
]

export const options = {
  license_key: 'gpl',
  selector: '.text-settings textarea',
  browser_spellcheck: true,
  inline: false,
  force_hex_color: 'always',
  schema: 'html5',
  allow_conditional_comments: false,
  paste_as_text: false,
  paste_merge_formats: true,
  paste_remove_styles_if_webkit: true,
  paste_webkit_styles: 'none',
  paste_retain_style_properties: '',
  paste_strip_class_attributes: 'all',
  paste_preprocess: function (plugin, args) {
    // Remove images immediately
    args.content = args.content.replace(/<img[^>]*>/gi, '')

    // Remove Word XML artifacts
    args.content = args.content.replace(/<\?xml[^>]*>/gi, '')
    args.content = args.content.replace(/<\/?\w+:[^>]*>/gi, '')

    // Remove Word conditional comments
    args.content = args.content.replace(
      /<!--\[if[^\]]*\]>[\s\S]*?<!\[endif\]-->/gi,
      ''
    )
  },
  highlight_on_focus: false,
  skin: false,
  doctype:
    '<!DOCTYPE html PUBLIC "-//W3C//DTD XHTML 1.0 Transitional//EN" "http://www.w3.org/TR/xhtml1/DTD/xhtml1-transitional.dtd">',
  plugins: [
    'lists',
    'link',
    'emoji',
    'tinycolor',
    'hidetoolbar',
    'contactattrs',
    'computedstyles',
    'placeholder',
    'listselectionguard',
  ].join(' '),
  toolbar1: 'blocks fontfamily fontsize',
  toolbar2:
    'bold italic underline strikethrough | alignleft aligncenter alignright | numlist bullist',
  toolbar3: [
    'link',
    'unlink',
    '|',
    'emoji',
    'contactattrs',
    'removeformat',
    '|',
    'tinycolor',
  ].join(' '),
  menubar: false,
  contextmenu: false,
  height: '100%',
  block_formats: 'Normal=p;Header 1=h1;Header 2=h2;Header 3=h3;Header 4=h4',
  content_style: [
    `body { font-family: Verdana,Arial,Helvetica,sans-serif; font-size: 11px; line-height: normal; }
    td, th { font-size: 11px; }
    .saturation-white {
      background: -webkit-linear-gradient(to right, #fff, rgba(255,255,255,0))!important;
      background: linear-gradient(to right, #fff, rgba(255,255,255,0))!important;
    }
    .saturation-black {
      background: -webkit-linear-gradient(to top, #000, rgba(0,0,0,0))!important;
      background: linear-gradient(to top, #000, rgba(0,0,0,0))!important;
    }
    `,
  ].join('\n'),
  font_family_formats: getFontFormats(),
  content_css: getFontContentCss(),
  font_size_formats:
    '10px 11px 12px 13px 14px 15px 16px 17px 18px 19px 20px 21px 22px 23px 24px 25px 26px 27px 28px 29px 30px 32px 36px 48px 60px 72px 96px',
  urlconverter_callback: function (url, node, on_save, name) {
    return addHttp(url, true)
  },
  valid_styles: {
    '*': 'color,font-size,text-decoration,font-family,text-align',
  },
  // Explicitly define which elements TinyMCE should allow (matches our allowlist)
  valid_elements:
    'p[style|data-inline|data-mce-*|class],' +
    'h1[style|data-inline|data-mce-*|class],' +
    'h2[style|data-inline|data-mce-*|class],' +
    'h3[style|data-inline|data-mce-*|class],' +
    'h4[style|data-inline|data-mce-*|class],' +
    'strong,b,' +
    'em,i,' +
    'u,' +
    's,strike,' +
    'a[style|href|data-inline|data-mce-*|target],' +
    'ul[style|data-inline|data-mce-*|class],' +
    'ol[style|data-inline|data-mce-*|class],' +
    'li[style|data-inline|data-mce-*|class],' +
    'br,' +
    'span[style|data-inline|data-mce-*|class]',
  placeholder: 'text-editor.placeholder',
  link_list: linkList,
  link_target_list: false,
  link_title: false,
  link_default_target: '_blank',
  branding: false,
  statusbar: false,
  keep_styles: false,
  formats: {
    underline: [
      {
        inline: 'span',
        styles: { 'text-decoration': 'underline' },
        classes: 'underline',
      },
    ],
  },
  init_instance_callback: (editor) => {
    editor.on('NodeChange', (e) => {
      const event = { ...e }

      if (
        event.element &&
        event.element.parentNode &&
        event.element.parentNode.tagName.toLowerCase() === 'li'
      ) {
        editor.selection.getSelectedBlocks().forEach((item) => {
          if (item.tagName.toLowerCase() === 'li') {
            if (item.contains(event.element) && event.element.previousSibling) {
              return
            }

            item.style.color = event.element.style.color

            item.setAttribute(
              'data-mce-style',
              updateStyleAttribute(item.getAttribute('data-mce-style'), {
                color: event.element.style.color,
              })
            )
          }
        })

        if (!event.element.previousSibling) {
          event.element.parentNode.style.color = event.element.style.color
          event.element.parentNode.setAttribute(
            'data-mce-style',
            updateStyleAttribute(
              event.element.parentNode.getAttribute('data-mce-style'),
              {
                color: event.element.style.color,
              }
            )
          )
        }
      }
    })
  },
}
